import { Platform } from 'react-native'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import semver from 'semver'

const RemoteConfigFeaturesLogin = types.model('RemoteConfigFeaturesLogin').props({
  mode: 'oauth',
  supportEmail: true,
  supportPhone: true,
  termsAndConditions: 'country',
  magicLink: false
})

const RemoteConfigFacesVideo = types.model('RemoteConfigFacesVideo').props({
  controls: true,
  allowSkip: false
})

const RemoteConfigFaces = types.model('RemoteConfigFaces').props({
  video: types.optional(RemoteConfigFacesVideo, {})
})

const RemoteConfigHeader = types.model('RemoteConfigHeader').props({
  inventory: types.optional(types.boolean, false),
  map: types.optional(types.boolean, false),
  scanner: types.optional(types.boolean, false),
  ar: types.optional(types.boolean, false),
  activity: types.optional(types.boolean, false),
  profile: types.optional(types.string, ''),
  logo: types.optional(types.boolean, false),
  category: types.optional(types.boolean, false),
  search: types.optional(types.boolean, false),
  sort: types.optional(types.boolean, false)
})

const RemoteConfigFooter = types.model('RemoteConfigFooter').props({
  inventory: types.optional(types.boolean, false),
  map: types.optional(types.boolean, false),
  scanner: types.optional(types.boolean, false),
  ar: types.optional(types.boolean, false)
})

const RemoteConfigInventory = types.model('RemoteConfigInventory').props({
  fqdn_whitelist: types.optional(types.array(types.string), []),
  tv_blacklist: types.optional(types.array(types.string), []),
  object_definition_blacklist: types.optional(types.array(types.string), []),
  splashContent: types.optional(types.boolean, false),
  pinned: types.optional(types.array(types.string), [])
})

const RemoteConfigVatom = types.model('RemoteConfigVatom').props({
  closeToInventory: types.optional(types.boolean, false)
})

const RemoteConfigLoginPopup = types.model('RemoteConfigLoginPopup').props({
  supportsVerify: types.optional(types.boolean, false)
})

const RemoteConfigClaim = types.model('RemoteConfigClaim').props({
  renderClaimPage: types.optional(types.boolean, false)
})

const RemoteConfigIncomingOverlay = types.model('RemoteConfigIncomingOverlay').props({
  enabled: types.optional(types.boolean, false)
})

const RemoteConfigAr = types.model('RemoteConfigAr').props({
  visibleRadius: types.optional(types.number, 500),
  maxItems: types.optional(types.number, 5),
  resizeObjects: types.optional(types.boolean, false),
  desiredRadius: types.optional(types.number, 1),
  scalingStrategy: types.optional(types.string, 'new'),
  distanceFromCamera: types.optional(types.number, 5),
  objectBaseHeight: types.optional(types.number, 2),
  minDistance: types.optional(types.number, 5),
  additionalRandomDistance: types.optional(types.number, 10),
  ambientLightColor: types.optional(types.string, 'transparent')
})

const RemoteConfigMapPickup = types.model('RemoteConfigMapPickup').props({
  enabled: types.optional(types.boolean, true),
  redirect: types.optional(types.string, 'ar')
})

const RemoteConfigMap = types.model('RemoteConfigMap').props({
  pickup: types.optional(RemoteConfigMapPickup, {})
})

const ForceUpdateModel = types
  .model({
    ios: types.maybe(types.string),
    android: types.maybe(types.string)
  })
  .views(self => ({
    shouldUpdate(version: string): boolean {
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        const minVersion = self[Platform.OS]
        if (!minVersion) return false
        if (semver.gte(version, minVersion)) {
          return false
        } else {
          return true
        }
      } else {
        return false
      }
    }
  }))

export const RemoteConfigFeatures = types.model('RemoteConfigFeatures').props({
  login: types.optional(RemoteConfigFeaturesLogin, {}),
  faces: types.optional(RemoteConfigFaces, {}),
  header: types.optional(RemoteConfigHeader, {}),
  footer: types.optional(RemoteConfigFooter, {}),
  inventory: types.optional(RemoteConfigInventory, {}),
  vatom: types.optional(RemoteConfigVatom, {}),
  loginPopup: types.optional(RemoteConfigLoginPopup, {}),
  claim: types.optional(RemoteConfigClaim, {}),
  incomingOverlay: types.optional(RemoteConfigIncomingOverlay, {}),
  fullScreenScroll: types.optional(types.boolean, false),
  campaignHeader: types.optional(types.boolean, false),
  ar: types.optional(RemoteConfigAr, {}),
  maps: types.optional(RemoteConfigMap, {})
})

export const RemoteConfigStore = types.model('RemoteConfigStore').props({
  appID: types.optional(types.string, 'd9fd1482-3625-4746-854f-726b9032d4ff'),
  appTitle: types.optional(types.string, 'Vatom Viewer'),
  viewerId: types.optional(types.string, '94JHkdj8jF83jfFF2LI8Q4'),
  features: types.optional(RemoteConfigFeatures, {}),
  forceUpdate: types.optional(ForceUpdateModel, {}),
  browserCompatbilityRegexes: types.optional(types.array(types.string), [])
})

export type RemoteConfigStoreType = Instance<typeof RemoteConfigStore>
export type RemoteConfigStoreSnapshot = SnapshotOut<typeof RemoteConfigStore>

// export type Entries<T> = {
//   [K in keyof T]: [K, T[K]]
// }[keyof T][]
type PickByValue<T, V> = Pick<T, { [K in keyof T]: T[K] extends V ? K : never }[keyof T]>
// type TestA = PickByValue<Test, 'a'>; // {includeMe: "a"; andMe: "a"}
// type IncludedKeys = keyof PickByValue<Test, 'a'>; // "includeMe" | "andMe"
export type Entries<T> = {
  [K in keyof T]: [keyof PickByValue<T, T[K]>, T[K]]
}[keyof T][]
